import React from "react";

/**
 * Reusable pill select with a leading "All" chip.
 *
 * An empty `value` means "All" (nothing filtered). Selecting "All" clears the
 * list. In `multiple` mode chips toggle independently; in single mode picking a
 * chip replaces the selection (clicking the active one clears back to All). The
 * value is always emitted as an array so callers have one shape either way.
 *
 * @param {Array}    options    [{ label, value }]
 * @param {Array}    value      selected values ([] = All)
 * @param {Function} onChange   (next: Array) => void
 * @param {boolean}  multiple   allow multiple selections (default true)
 * @param {string}   allLabel   label for the leading chip (default "All")
 * @param {boolean}  disabled
 * @param {string}   appearance "outlined" (default) | "pill" — see APPEARANCES
 */
const CHIP_BASE =
  "inline-flex items-center border-solid border-1 px-2 py-px text-sm leading-5 transition-colors";

/**
 * Visual variants. "outlined" is the original look — every existing caller
 * relies on it, so it stays the default. "pill" is the fully-rounded, filled
 * variant specified by the consent Audit log design (antd Blue/1-3-7 for the
 * active chip, Tag defaultBg for the idle one).
 */
const APPEARANCES = {
  outlined: {
    shape: "rounded-[4px]",
    off: "border-[#D9D9D9] bg-white text-black-88",
    on: "border-[#91CAFF] bg-[#E6F4FF] text-twLinkColor",
  },
  pill: {
    shape: "rounded-full",
    off: "border-transparent bg-[#FAFAFA] text-black-88",
    on: "border-[#91CAFF] bg-[#E6F4FF] text-[#0958D9]",
  },
};

const ChipCheckboxGroup = ({
  options = [],
  value = [],
  onChange,
  multiple = true,
  allLabel = "All",
  disabled = false,
  appearance = "outlined",
}) => {
  const selected = value || [];
  const allActive = selected.length === 0;

  const emit = next => !disabled && onChange?.(next);

  const pick = val => {
    if (disabled) return;
    const isSelected = selected.includes(val);
    if (!multiple) {
      emit(isSelected ? [] : [val]);
      return;
    }
    emit(isSelected ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const theme = APPEARANCES[appearance] || APPEARANCES.outlined;

  const chipClass = active =>
    `${CHIP_BASE} ${theme.shape} ${active ? theme.on : theme.off} ${
      disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
    }`;

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        aria-pressed={allActive}
        className={chipClass(allActive)}
        onClick={() => emit([])}
      >
        {allLabel}
      </button>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={selected.includes(opt.value)}
          className={chipClass(selected.includes(opt.value))}
          onClick={() => pick(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

export default ChipCheckboxGroup;
